import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Box,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@material-ui/core";

import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

// @material-ui/icons
import Visibility from "@material-ui/icons/Visibility";
import Business from "@material-ui/icons/Business";
import Assignment from "@material-ui/icons/Assignment";
import AccessTime from "@material-ui/icons/AccessTime";
import Update from "@material-ui/icons/Update";
import Code from "@material-ui/icons/Code";

// Material Dashboard components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

import styles from "assets/jss/material-dashboard-react/views/dashboardStyle.js";
import { fetchInterestedLeads } from "views/Apis/AfterLogin";

const useStyles = makeStyles(styles);

export default function InterestedLeads() {
  const classes = useStyles();

  const [leads, setLeads] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const [selectedLead, setSelectedLead] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Fetch interested leads data
  const fetchInterestedLeadsApi = () => {
    fetchInterestedLeads(page, size)
      .then((res) => {
        if (res?.leads) {
          setLeads(res.leads);
          setTotalPages(res.totalPages);
          setTotalItems(res.totalItems);
        }
      })
      .catch((err) => console.error("Error fetching interested leads:", err.message));
  };

  useEffect(() => {
    fetchInterestedLeadsApi();
  }, [page, size]);

  const handleShowLeadDetails = (lead) => {
    setSelectedLead(lead);
    setOpenDialog(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const getCallStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'answered': return 'primary';
      case 'busy': return 'secondary';
      case 'not_answered': return 'default';
      default: return 'default';
    }
  };

  const tableHead = [
    "Name",
    "Email",
    "Phone",
    "Company",
    "Employee Name",
    "Actions"
  ];

  return (
    <div>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="success">
              <h4 className={classes.cardTitleWhite}>Interested Leads List</h4>
              <p className={classes.cardCategoryWhite}>
                Page {page + 1} of {totalPages} | Total: {totalItems} leads
              </p>
            </CardHeader>
            <CardBody>
              {/* Pagination Controls */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  <Button
                    disabled={page <= 0}
                    onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                  >
                    Previous
                  </Button>
                  <Button
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((prev) => prev + 1)}
                  >
                    Next
                  </Button>
                </div>

                <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                  <InputLabel>Page Size</InputLabel>
                  <Select
                    value={size}
                    onChange={(e) => {
                      setSize(e.target.value);
                      setPage(0); // reset to first page
                    }}
                    label="Page Size"
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={30}>30</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
              </div>

              <div style={{ overflowX: "auto" }}>
                <MuiTable>
                  <TableHead>
                    <TableRow>
                      {tableHead.map((head, idx) => (
                        <TableCell key={idx}>{head}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {leads.map((lead) => (
                      <TableRow key={lead.id}>
                        <TableCell>{lead.name || "-"}</TableCell>
                        <TableCell>{lead.email || "-"}</TableCell>
                        <TableCell>{lead.phoneNumber || "-"}</TableCell>
                        <TableCell>{lead.companyName || "-"}</TableCell>
                        <TableCell>{lead.employeeName || "-"}</TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => handleShowLeadDetails(lead)}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </MuiTable>
              </div>

            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* Lead Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedLead ? `Lead Details - ${selectedLead.name}` : "Lead Details"}
        </DialogTitle>
        <DialogContent>
          {selectedLead && (
            <div>
              {/* Basic Information */}
              <Typography variant="h6" gutterBottom>
                <Business style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Basic Information
              </Typography>
              <GridContainer spacing={2}>
                <GridItem xs={12} md={6}>
                  <Typography variant="body1" paragraph>
                    <strong>Name:</strong> {selectedLead.name || 'N/A'}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Email:</strong> {selectedLead.email || 'N/A'}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Phone:</strong> {selectedLead.phoneNumber || 'N/A'}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Alternative Contact:</strong> {selectedLead.alternativeContact || 'N/A'}
                  </Typography>
                </GridItem>
                <GridItem xs={12} md={6}>
                  <Typography variant="body1" paragraph>
                    <strong>Company:</strong> {selectedLead.companyName || 'N/A'}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Company Type:</strong> {selectedLead.companyType || 'N/A'}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Location:</strong> {selectedLead.location || 'N/A'}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Address:</strong> {selectedLead.address || 'N/A'}
                  </Typography>
                </GridItem>
              </GridContainer>

              <Typography variant="body1" paragraph>
                <strong>Source:</strong> {selectedLead.source || 'N/A'}
              </Typography>

              <Divider style={{ margin: '16px 0' }} />

              {/* Lead Status Information */}
              <Typography variant="h6" gutterBottom>
                <Assignment style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Lead Status Information
              </Typography>
              <GridContainer spacing={2}>
                <GridItem xs={12} md={6}>
                  <Typography variant="body1" paragraph>
                    <strong>Lead Status:</strong>
                    <Chip
                      label={selectedLead.leadStatus || 'N/A'}
                      color="success"
                      size="small"
                      style={{ marginLeft: 8 }}
                    />
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Call Status:</strong>
                    <Chip
                      label={selectedLead.callStatus || 'N/A'}
                      color={getCallStatusColor(selectedLead.callStatus)}
                      size="small"
                      style={{ marginLeft: 8 }}
                    />
                  </Typography>
                </GridItem>
                <GridItem xs={12} md={6}>
                  <Typography variant="body1" paragraph>
                    <strong>Assigned Employee:</strong> {selectedLead.employeeName || 'N/A'}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Employee ID:</strong> {selectedLead.employeeId || 'N/A'}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Last Called:</strong> {selectedLead.lastCalledDate ? new Date(selectedLead.lastCalledDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'N/A'}
                  </Typography>
                </GridItem>
              </GridContainer>

              {/* Lead Services */}
              {selectedLead.leadServiceDTOS && selectedLead.leadServiceDTOS.length > 0 && (
                <>
                  <Divider style={{ margin: '16px 0' }} />
                  <Typography variant="h6" gutterBottom>
                    <Code style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    Lead Services
                  </Typography>
                  {selectedLead.leadServiceDTOS.map((service, index) => (
                    <Box key={service.id} style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                      <Typography variant="body2">
                        <strong>Service #{index + 1}</strong>
                      </Typography>
                      <Typography variant="body2">
                        <strong>Status:</strong> {service.leadStatus || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Standard Remarks:</strong> {service.standardRemarks || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Created:</strong> {service.createdAt ? new Date(service.createdAt).toLocaleDateString() : 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Updated:</strong> {service.updatedAt ? new Date(service.updatedAt).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}

              {/* Lead Activities */}
              {selectedLead.leadActivityInterestedDTOS && selectedLead.leadActivityInterestedDTOS.length > 0 && (
                <>
                  <Divider style={{ margin: '16px 0' }} />
                  <Typography variant="h6" gutterBottom>
                    <AccessTime style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    Recent Activities ({selectedLead.leadActivityInterestedDTOS.length})
                  </Typography>
                  {selectedLead.leadActivityInterestedDTOS.map((activity, index) => (
                    <Box key={activity.id} style={{ marginBottom: 16, padding: 12, backgroundColor: '#f0f8ff', borderRadius: 4 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
                        <Typography variant="body2">
                          <strong>Activity #{index + 1}</strong>
                        </Typography>
                        <Chip
                          label={activity.status ? 'Active' : 'Inactive'}
                          color={activity.status ? 'primary' : 'default'}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2">
                        <strong>Call Status:</strong> {activity.callStatus || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Lead Status:</strong> {activity.leadStatus || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Comment:</strong> {activity.comment || 'No comments'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Created:</strong> {activity.createdAt ? new Date(activity.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Updated:</strong> {activity.updatedAt ? new Date(activity.updatedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'N/A'}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}

              <Divider style={{ margin: '16px 0' }} />

              {/* Timestamps */}
              <Typography variant="h6" gutterBottom>
                <Update style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Timeline
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Created:</strong> {selectedLead.createdAt ? new Date(selectedLead.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'N/A'}
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Last Updated:</strong> {selectedLead.updatedAt ? new Date(selectedLead.updatedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'N/A'}
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}